import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Request } from '../requests/types';
import { deleteComment, fetchAll, updateComment } from '.';
import type { Comment, DeleteCommentDto, UpdateCommentDto } from './types';

export const useGetComments = (
  projectId: string,
  requestId: string,
  { enabled }: { enabled: boolean },
) => {
  return useQuery({
    queryKey: ['project', projectId, 'requests', requestId, 'comments'],
    queryFn: () => fetchAll(projectId, requestId),
    enabled,
  });
};

export const useDeleteComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ requestId, commentId }: DeleteCommentDto) => deleteComment(requestId, commentId),
    onSuccess: (_, { requestId, projectId, commentId, status }) => {
      queryClient.setQueryData(
        ['project', projectId, 'requests', requestId, 'comments'],
        (oldComments: Comment[]) => oldComments.filter((item) => item.id !== commentId),
      );

      queryClient.setQueryData(
        ['requests', 'project', projectId, status],
        (oldRequests: Request[]) =>
          oldRequests.map((item) =>
            item.id === requestId ? { ...item, commentCount: item.commentCount - 1 } : item,
          ),
      );
    },
  });
};

export const useUpdateComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ requestId, commentId, data }: UpdateCommentDto) =>
      updateComment(requestId, commentId, data),
    onSuccess: (_, { requestId, projectId, commentId, data }) => {
      queryClient.setQueryData(
        ['project', projectId, 'requests', requestId, 'comments'],
        (oldComments: Comment[]) =>
          oldComments.map((comment) =>
            comment.id === commentId ? { ...comment, ...data } : comment,
          ),
      );
    },
  });
};
